<template>
  <div class="relative">
    <div class="w-full mt-2 mb-3 rounded-md bottom-full">
      <div class="relative flex items-center justify-between px-10 select-none">
        <div class="flex flex-col w-[100px]" @click="activeScrollbar = 'hour'">
          <div
            class="h-6"
            :class="{
              'bg-gradient-to-t from-cyan-light to-white': activeScrollbar === 'hour',
            }"></div>
          <div
            class="flex flex-col h-[194px] gap-4 overflow-y-auto"
            :class="{ 'bg-cyan-light': activeScrollbar === 'hour' }"
            ref="scrollHourContainer"
            id="hourContainer"
            @scroll="handleScroll">
            <div
              v-for="(hour, index) in hours"
              :key="index"
              class="flex items-center justify-center w-full h-5 font-light text-slate-500"
              :class="[
                {
                  'min-h-[50px]  bg-white text-2xl ': hour === selectedHour,
                  'text-theme-500': hour === selectedHour && activeScrollbar === 'hour',
                  'text-slate-950': hour === selectedHour && activeScrollbar !== 'hour',
                },
                activeScrollbar === 'hour' ? 'cursor-pointer' : 'pointer-events-none',
              ]"
              @click="onHourClick(hour)">
              {{ hour }}
            </div>
          </div>
          <div
            class="h-6"
            :class="{
              'bg-gradient-to-b from-cyan-light to-white ': activeScrollbar === 'hour',
            }"></div>
        </div>
        <div class="absolute text-2xl transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
          :
        </div>
        <div class="flex flex-col w-[100px]" @click="activeScrollbar = 'minute'">
          <div
            class="h-6"
            :class="{
              'bg-gradient-to-t from-cyan-light to-white': activeScrollbar === 'minute',
            }"></div>
          <div
            class="flex flex-col h-[194px] gap-4 overflow-y-auto"
            :class="{ 'bg-cyan-light': activeScrollbar == 'minute' }"
            ref="scrollMinuteContainer"
            id="minuteContainer"
            @scroll="handleScroll">
            <div
              v-for="(minute, index) in minutes"
              :key="index"
              class="flex items-center justify-center w-full h-5 font-light text-slate-500"
              :class="[
                {
                  'min-h-[50px]  bg-white text-2xl ': minute === selectedMinute,
                  'text-theme-500': minute === selectedMinute && activeScrollbar === 'minute',
                  'text-theme-950': minute === selectedMinute && activeScrollbar !== 'minute',
                },
                activeScrollbar === 'minute' ? 'cursor-pointer' : 'pointer-events-none',
              ]"
              @click="onMinuteClick(minute)">
              {{ minute }}
            </div>
          </div>
          <div
            class="h-6"
            :class="{
              'bg-gradient-to-b from-cyan-light to-white': activeScrollbar === 'minute',
            }"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';

const time = defineModel<string | undefined>();
const props = defineProps<{
  recenter?: boolean;
}>();

const selectedMinute = ref('00');
const selectedHour = ref('00');

const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
hours.unshift('21', '22', '23');
hours.push('00', '01', '02');

const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
minutes.unshift('57', '58', '59');
minutes.push('00', '01', '02');

const scrollHourContainer = ref<Element>();
const scrollMinuteContainer = ref<Element>();

const activeScrollbar = ref<'hour' | 'minute'>('hour');

const internalUpdate = ref(false);

async function setInitialTime(time: string): Promise<void> {
  if (internalUpdate.value) return;
  if (time) {
    const [hour, minute] = time.split(':');

    if (scrollHourContainer.value) {
      await setHour(hour);
    }
    if (scrollMinuteContainer.value) {
      await setMinute(minute);
    }

    if (hour != selectedHour.value) {
      internalUpdate.value = true;
      selectedHour.value = hour;
    }
    if (minute != selectedMinute.value) {
      internalUpdate.value = true;
      selectedMinute.value = minute;
    }
    internalUpdate.value = false;
  }
}

watch(
  () => time.value,
  (newVal: string | undefined) => {
    if (newVal && !internalUpdate.value) {
      nextTick(() => {
        setInitialTime(newVal);
      });
    }
  },
  {
    immediate: true,
  }
);

watch(
  () => props.recenter,
  (newVal: boolean) => {
    if (newVal && time.value && !internalUpdate.value) {
      setInitialTime(time.value);
    }
  }
);

watch([selectedHour, selectedMinute], ([hour, minute]) => {
  time.value = `${hour}:${minute}`;
});

const itemHeight = 36;

function setHour(hour: string) {
  if (!scrollHourContainer.value) return;
  scrollHourContainer.value.scrollTop = 3 * itemHeight;
  const position = Number(hour) >= 21 && Number(hour) <= 23 ? 1.2 : 2;
  scrollHourContainer.value.scrollTo({
    top: scrollHourContainer.value.scrollTop + itemHeight * (Number(hour) - position),
    behavior: 'smooth',
  });
}

function setMinute(minute: string) {
  if (!scrollMinuteContainer.value) return;
  scrollMinuteContainer.value.scrollTop = 3 * itemHeight;
  const position = Number(minute) >= 57 && Number(minute) <= 59 ? 1.2 : 2;

  scrollMinuteContainer.value.scrollTo({
    top: scrollMinuteContainer.value.scrollTop + itemHeight * (Number(minute) - position),
    behavior: 'smooth',
  });
}

function handleScroll() {
  const scrollContainer =
    activeScrollbar.value === 'hour' ? scrollHourContainer.value : scrollMinuteContainer.value;
  if (!scrollContainer) return;
  let scrollTop = scrollContainer.scrollTop;

  // infinite scroll
  if (scrollHourContainer.value?.scrollTop == 0) {
    scrollContainer.scrollTop += 22 * itemHeight;
  } else if (Math.ceil(scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight) {
    scrollContainer.scrollTop -= 22 * itemHeight;
  }
}

async function onHourClick(hour: string) {
  internalUpdate.value = true;
  selectedHour.value = hour;
  await setHour(hour);
  internalUpdate.value = false;
}

async function onMinuteClick(minute: string) {
  internalUpdate.value = true;
  selectedMinute.value = minute;
  await setMinute(minute);
  internalUpdate.value = false;
}
</script>

<style scoped>
::-webkit-scrollbar {
  width: 0px;
  height: 0;
}

::-o-scrollbar {
  width: 0px;
  height: 0;
}

::-ms-scrollbar {
  width: 0px;
  height: 0;
}
</style>
